import React, { useState, useRef, useEffect } from 'react';
import { FiUser, FiCpu, FiSearch, FiAward, FiCode, FiTool } from 'react-icons/fi';

import { useQuery } from '@tanstack/react-query';
import * as personaService from '../../services/personaService';
import { Persona } from '../../types/persona';

interface PersonaSelectorProps {
  selectedPersonaId: string;
  onSelectPersona: (personaId: string) => void;
  darkMode: boolean;
  compact?: boolean;
}

const PersonaSelector: React.FC<PersonaSelectorProps> = ({ 
  selectedPersonaId, 
  onSelectPersona, 
  darkMode,
  compact = false
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredPersona, setHoveredPersona] = useState<Persona | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch personas from API
  const { data: personas, isLoading, error } = useQuery(
    ['personas'],
    async () => {
      try {
        const personas = await personaService.getPersonas();
        return personas;
      } catch (error) {
        console.error("Error fetching personas:", error);
        // Return some default personas as fallback
        return [
          {
            id: 'System_Architect',
            name: 'System Architect',
            primarySpecialization: 'Full-stack architecture and system design',
            tags: ['Architecture', 'Full-Stack']
          },
          {
            id: 'React_Architect',
            name: 'React Architect',
            primarySpecialization: 'React component design and frontend architecture',
            tags: ['Frontend', 'React']
          },
          {
            id: 'Python_Backend',
            name: 'Python Backend Developer',
            primarySpecialization: 'Python API and service development',
            tags: ['Backend', 'Python']
          },
          {
            id: 'FullStack_Dev',
            name: 'Full Stack Developer',
            primarySpecialization: 'End-to-end application development',
            tags: ['Full-Stack', 'JavaScript', 'Python']
          },
          {
            id: 'UI_Designer',
            name: 'UI Designer',
            primarySpecialization: 'User interface and component design',
            tags: ['Frontend', 'UI/UX']
          }
        ];
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Get the selected persona object
  const selectedPersona = personas?.find(p => p.id === selectedPersonaId) || {
    id: selectedPersonaId || 'unknown',
    name: selectedPersonaId || 'Unknown Persona',
    primarySpecialization: 'No description available',
    tags: []
  };

  // Filter personas based on search term
  const filteredPersonas = personas?.filter(persona => 
    persona.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (persona.primarySpecialization || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (persona.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
  ) || [];

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHoveredPersona(null); // Clear hover state when dropdown closes
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const selectPersona = (persona: Persona) => {
    onSelectPersona(persona.id);
    setIsDropdownOpen(false);
    setSearchTerm('');
    setHoveredPersona(null); // Clear hover state when selecting
  };

  // Handle mouse enter on persona item
  const handleMouseEnter = (persona: Persona, event: React.MouseEvent) => {
    setHoveredPersona(persona);
    
    // Calculate position for hover card
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const dropdownRect = dropdownRef.current?.getBoundingClientRect();
    
    if (dropdownRect) {
      // Position to the left of the dropdown
      setHoverPosition({
        x: dropdownRect.left - 330, // 330px accounts for the hover card width (320px) plus a small gap
        y: rect.top - 20 // Align with the top of the hovered item
      });
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setHoveredPersona(null);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={`flex items-center p-2 rounded-md ${
        darkMode 
          ? 'bg-gray-700 border border-gray-600 text-white' 
          : 'bg-white border border-gray-300 text-gray-800'
      }`}>
        <div className="animate-pulse flex space-x-2 items-center w-full">
          <div className={`w-6 h-6 rounded-full ${darkMode ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
          <div className="flex-1">
            <div className={`h-4 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'} rounded w-3/4`}></div>
            {!compact && <div className={`h-3 ${darkMode ? 'bg-gray-600' : 'bg-gray-300'} rounded w-1/2 mt-1`}></div>}
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className={`flex items-center p-2 rounded-md ${
        darkMode 
          ? 'bg-red-900 border border-red-800 text-white' 
          : 'bg-red-50 border border-red-200 text-red-800'
      }`}>
        <div className="text-sm">Failed to load personas</div>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <div 
        className={`flex items-center p-2 rounded-md cursor-pointer ${
          darkMode 
            ? 'bg-gray-700 border border-gray-600 text-white hover:bg-gray-650' 
            : 'bg-white border border-gray-300 text-gray-800 hover:bg-gray-50'
        }`}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
      >
        <div className={`w-6 h-6 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} flex items-center justify-center mr-2 flex-shrink-0`}>
          <FiUser className="text-white" size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className={`font-medium truncate ${compact ? 'text-sm' : ''}`}>{selectedPersona.name}</div>
          {!compact && (
            <div className="text-xs truncate text-gray-400">
              {selectedPersona.primarySpecialization || selectedPersona.shortDescription || ''}
            </div>
          )}
        </div>
      </div>
      
      {isDropdownOpen && (
        <div className={`absolute z-50 mt-1 w-[400px] ${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-md shadow-lg max-h-80 overflow-auto border ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
          {/* Search input */}
          <div className={`sticky top-0 p-2 ${darkMode ? 'bg-gray-800' : 'bg-white'} border-b ${darkMode ? 'border-gray-700' : 'border-gray-300'}`}>
            <div className="relative">
              <input
                type="text"
                className={`w-full p-2 pl-8 rounded-md ${
                  darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-300'
                } border focus:outline-none focus:ring-1 focus:ring-blue-500`}
                placeholder="Search personas..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>
          
          {/* Filtered persona options */}
          {filteredPersonas.length > 0 ? (
            filteredPersonas.map(persona => (
              <div 
                key={persona.id} 
                className={`px-3 py-3 cursor-pointer ${
                  selectedPersonaId === persona.id 
                    ? darkMode ? 'bg-blue-800' : 'bg-blue-100' 
                    : darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                onClick={() => selectPersona(persona)}
                onMouseEnter={(e) => handleMouseEnter(persona, e)}
                onMouseLeave={handleMouseLeave}
              >
                <div className="flex items-start">
                  <div className={`w-6 h-6 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'} flex items-center justify-center mr-2 flex-shrink-0 mt-0.5`}>
                    <FiUser className="text-white" size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-base">{persona.name}</div>
                    <div className="flex items-center mt-1">
                      <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} line-clamp-2 flex-1`}>
                        {persona.primarySpecialization || persona.shortDescription || ''}
                      </div>
                      <div className="flex flex-wrap gap-1 ml-2">
                        {persona.tags.slice(0, 2).map(tag => (
                          <span key={tag} className={`px-2 py-0.5 text-xs rounded-full truncate ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>{tag}</span>
                        ))}
                        {persona.tags.length > 2 && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            darkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}>+{persona.tags.length - 2}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={`px-4 py-3 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              No matching personas
            </div>
          )}
        </div>
      )}
      
      {/* Hover Card */}
      {hoveredPersona && (
        <div 
          className={`fixed z-50 w-80 rounded-lg shadow-xl overflow-hidden ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
          style={{ 
            left: `${hoverPosition.x}px`, 
            top: `${hoverPosition.y}px`,
            maxHeight: '400px',
            overflowY: 'auto'
          }}
        >
          {/* Header */}
          <div className={`p-4 ${darkMode ? 'bg-blue-900' : 'bg-blue-50'}`}>
            <div className="flex items-center">
              <div className={`w-10 h-10 rounded-full ${darkMode ? 'bg-blue-700' : 'bg-blue-500'} flex items-center justify-center mr-3`}>
                <FiUser className="text-white" size={20} />
              </div>
              <div>
                <h3 className={`font-bold text-lg ${darkMode ? 'text-white' : 'text-blue-800'}`}>
                  {hoveredPersona.name}
                </h3>
                <p className={`text-sm ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>
                  {hoveredPersona.primarySpecialization || hoveredPersona.shortDescription || 'Specialized AI Persona'}
                </p>
              </div>
            </div>
          </div>
          
          {/* Body */}
          <div className="p-4">
            {/* Capabilities */}
            {hoveredPersona.capabilities && (
              <div className="mb-4">
                <h4 className={`text-sm font-semibold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FiAward className="mr-1" /> Capabilities
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(hoveredPersona.capabilities).map(([key, value]) => (
                    <div key={key} className="flex flex-col">
                      <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'} capitalize`}>{key}</span>
                      <div className={`w-full h-2 rounded-full mt-1 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                        <div 
                          className={`h-2 rounded-full ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`} 
                          style={{ width: `${(value as number) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tags */}
            {hoveredPersona.tags && hoveredPersona.tags.length > 0 && (
              <div className="mb-4">
                <h4 className={`text-sm font-semibold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FiCode className="mr-1" /> Specializations
                </h4>
                <div className="flex flex-wrap gap-1">
                  {hoveredPersona.tags.map(tag => (
                    <span 
                      key={tag} 
                      className={`px-2 py-1 text-xs rounded-full ${
                        darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Tools */}
            {hoveredPersona.allowedTools && hoveredPersona.allowedTools.length > 0 && (
              <div className="mb-4">
                <h4 className={`text-sm font-semibold mb-2 flex items-center ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FiTool className="mr-1" /> Tools
                </h4>
                <div className="flex flex-wrap gap-1">
                  {hoveredPersona.allowedTools.slice(0, 5).map(tool => (
                    <span 
                      key={tool} 
                      className={`px-2 py-1 text-xs rounded-full ${
                        darkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {tool}
                    </span>
                  ))}
                  {hoveredPersona.allowedTools.length > 5 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      darkMode ? 'bg-gray-700' : 'bg-gray-200'
                    }`}>
                      +{hoveredPersona.allowedTools.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}
            
            {/* Description */}
            {hoveredPersona.fullDescription && (
              <div>
                <h4 className={`text-sm font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  About
                </h4>
                <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  {hoveredPersona.fullDescription.length > 200 
                    ? hoveredPersona.fullDescription.substring(0, 200) + '...' 
                    : hoveredPersona.fullDescription}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PersonaSelector;
